import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlayCircle, PauseCircle, CheckCircle2, Plus, ShieldCheck, Sparkles, Printer, History as HistoryIcon, Trash2, ClipboardList } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { useToast } from "../lib/toast";
import { StatusBadge } from "../components/StatusBadge";
import { DataTable, type Column } from "../components/DataTable";
import { PartIcon } from "../lib/partIcons";
import { SAFETY_CHECK_KEYS, type SafetyChecks, type SafetyCheckState, type RepairStatus, type PartUsed } from "@sbd-tcic/shared-types";

interface RepairOrderDetailDto {
  id: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  customerContact: string;
  equipment: string;
  modelNumber: string;
  serialNumber: string;
  dateCode: string;
  serviceType: string;
  grcPrice: number;
  grcIsPlaceholder: boolean;
  priority: string;
  status: RepairStatus;
  promisedDate: string;
  createdDate: string;
  deliveryMethod: string;
  technician: string | null;
  laborRate: number;
  laborHours: number;
  toolFailure: string;
  publicNotes: string;
  inspectedBy: string;
  inspectionDate: string | null;
  safetyChecks: SafetyChecks;
  partsUsed: PartUsed[];
}

interface HistoryRow {
  id: string;
  actionType: string;
  timestamp: string;
  detail: Record<string, unknown> | null;
  actorName: string | null;
}

interface PartCatalogRow {
  partNumber: string;
  description: string;
  category?: string;
  available: number;
  status: string;
  listPrice: number;
  stdCost: number;
}

/**
 * Real-catalog part picker — replaces free-text part-number entry (which let a typo or made-up
 * number reach the backend, previously surfacing as an unhandled crash before the backend was
 * hardened to reject it cleanly). Modeled on Pos.tsx's `CustomerSearchField`: same debounced
 * search-as-you-type dropdown, but also opens on focus with an empty query so a rep can scroll the
 * catalog instead of typing, matching both interaction modes asked for. Reuses the existing
 * Inventory parts search (`GET /api/v1/parts`, `inventory.read` — already held by every role that
 * can reach this screen) rather than adding a new endpoint for the same real data.
 */
function PartCatalogPicker({ value, onChange, onSelect }: { value: string; onChange: (v: string) => void; onSelect: (p: PartCatalogRow) => void }) {
  const [results, setResults] = useState<PartCatalogRow[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      void api.get<PartCatalogRow[]>(`/parts?query=${encodeURIComponent(value)}`).then(setResults);
    }, 200);
    return () => clearTimeout(t);
  }, [value, open]);

  return (
    <div className="relative col-span-2 sm:col-span-1">
      <input
        className="input"
        placeholder="Part # (required)"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full sm:w-80 card shadow-pop max-h-64 overflow-y-auto">
          {results.map((p) => (
            <button
              key={p.partNumber}
              type="button"
              className="w-full flex items-center gap-2.5 text-left px-3 py-2 hover:bg-surface border-b border-border last:border-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(p);
                setOpen(false);
              }}
            >
              <PartIcon category={p.category} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-xs truncate">
                  {p.partNumber} — {p.description}
                </div>
                <div className="text-[11px] text-ink-muted">{p.status}</div>
              </div>
              <div className="text-xs font-bold flex-shrink-0">${p.listPrice.toFixed(2)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const SAFETY_LABELS: Record<(typeof SAFETY_CHECK_KEYS)[number], string> = {
  leakage: "Leakage Current Test",
  ground: "Ground Continuity",
  hipot1000: "Hipot @ 1000V",
  hipot2500: "Hipot @ 2500V",
  tested: "Functional Test",
  cleaned: "Cleaned & Inspected",
  installedSafety: "Safety Devices Installed",
};

const ACTION_LABELS: Record<string, string> = {
  "repair_orders.create": "Repair order created",
  "repair_orders.add_part": "Part added",
  "repair_orders.remove_part": "Part removed",
  "repair_orders.complete": "Repair marked complete",
  "repair_orders.complete_payment": "Payment collected & order closed",
};

type Tab = "Work Order" | "Safety Checks & Inspections" | "History";
const TABS: Tab[] = ["Work Order", "Safety Checks & Inspections", "History"];

export function RepairOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [ro, setRo] = useState<RepairOrderDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>("Work Order");

  const [partNumber, setPartNumber] = useState("");
  const [partDesc, setPartDesc] = useState("");
  const [partQty, setPartQty] = useState(1);
  const [partStdCost, setPartStdCost] = useState(0);
  const [partListPrice, setPartListPrice] = useState(0);
  const [removeBusyId, setRemoveBusyId] = useState<string | null>(null);

  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);

  const [history, setHistory] = useState<HistoryRow[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const data = await api.get<RepairOrderDetailDto>(`/repair-orders/${id}`);
    setRo(data);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadHistory = useCallback(async () => {
    if (!id) return;
    setHistoryLoading(true);
    try {
      setHistory(await api.get<HistoryRow[]>(`/repair-orders/${id}/history`));
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load history.", "error");
    } finally {
      setHistoryLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (tab === "History" && history === null) void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const allPass = ro ? SAFETY_CHECK_KEYS.every((k) => ro.safetyChecks[k] === "Pass") : false;

  async function setCheck(key: string, value: SafetyCheckState) {
    if (!id) return;
    await api.patch(`/repair-orders/${id}/safety-checks`, { key, value });
    await load();
  }

  async function addPart() {
    if (!id) return;
    // Previously a silently-disabled button gave no indication *why* — reported twice as
    // "the button doesn't do anything." Now it's always clickable and explains what's missing.
    if (!partNumber.trim()) {
      toast("Enter a part number before adding.", "error");
      return;
    }
    if (partQty <= 0) {
      toast("Quantity must be at least 1.", "error");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/repair-orders/${id}/parts`, { partNumber: partNumber.trim(), description: partDesc.trim(), qty: partQty, stdCost: partStdCost, listPrice: partListPrice });
      setPartNumber("");
      setPartDesc("");
      setPartQty(1);
      setPartStdCost(0);
      setPartListPrice(0);
      await load();
      toast("Part added to repair order.", "success");
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to add part.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function removePart(partLineId: string) {
    if (!id) return;
    setRemoveBusyId(partLineId);
    try {
      await api.del(`/repair-orders/${id}/parts/${partLineId}`);
      await load();
      toast("Part removed from repair order.", "success");
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to remove part.", "error");
    } finally {
      setRemoveBusyId(null);
    }
  }

  async function transition(action: "start" | "pause" | "complete") {
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`/repair-orders/${id}/${action}`);
      if (action === "complete") {
        toast("Repair marked complete.", "success");
        navigate(`/repair-orders/${id}/payment`);
        return;
      }
      await load();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Action failed.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function generateAiSummary() {
    if (!id) return;
    setAiBusy(true);
    try {
      const { summary } = await api.post<{ summary: string }>(`/repair-orders/${id}/ai-summary`);
      setAiSummary(summary);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to generate a summary right now.", "error");
    } finally {
      setAiBusy(false);
    }
  }

  if (loading || !ro) return <div className="text-ink-muted text-sm py-10 text-center">Loading repair order…</div>;

  const partColumns: Column<PartUsed>[] = [
    { key: "image", header: "Image", render: (p) => <PartIcon category={p.category ?? undefined} /> },
    { key: "item", header: "Item #", render: (p) => ro.partsUsed.findIndex((x) => x.id === p.id) + 1 },
    { key: "material", header: "Material #", render: (p) => <span className="font-mono text-xs">{p.part}</span> },
    { key: "desc", header: "Description", render: (p) => p.desc },
    { key: "bin", header: "Bin", render: (p) => <span className="font-mono text-xs text-ink-muted">{p.bin ?? "—"}</span> },
    { key: "atp", header: "ATP", render: (p) => p.available ?? "—" },
    { key: "stdCost", header: "Std Cost", render: (p) => `$${p.stdCost.toFixed(2)}` },
    { key: "listPrice", header: "List Price", render: (p) => `$${p.listPrice.toFixed(2)}` },
    { key: "qty", header: "Qty", render: (p) => p.qty },
    { key: "ext", header: "Ext Price", render: (p) => <span className="font-semibold">${(p.qty * p.listPrice).toFixed(2)}</span> },
    {
      key: "actions",
      header: "",
      render: (p) =>
        ro.status !== "Complete" ? (
          <button aria-label="Remove part" disabled={removeBusyId === p.id} className="text-ink-muted hover:text-status-red" onClick={() => void removePart(p.id)}>
            <Trash2 size={14} />
          </button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="card p-5">
        <div className="flex items-start justify-between mb-3.5 flex-wrap gap-2">
          <div>
            <div className="text-lg font-extrabold">{ro.id}</div>
            <div className="text-sm text-ink-muted">
              {ro.equipment} ({ro.modelNumber} / SN {ro.serialNumber})
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <StatusBadge status={ro.status} />
            <button className="btn-outline" onClick={() => window.print()}>
              <Printer size={15} /> Print RO
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          <div className="border border-border rounded-lg p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink-muted mb-1.5">Customer Information</div>
            <div className="text-sm font-semibold">{ro.customerName}</div>
            <div className="text-xs text-ink-muted mt-0.5">{ro.customerContact}</div>
            <div className="text-xs text-ink-muted">{ro.customerPhone}</div>
            <div className="text-xs text-ink-muted">{ro.customerAddress}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink-muted mb-1.5">Equipment / Tool Information</div>
            <div className="text-sm font-semibold">{ro.equipment}</div>
            <div className="text-xs text-ink-muted mt-0.5">Model {ro.modelNumber}</div>
            <div className="text-xs text-ink-muted">Serial {ro.serialNumber}</div>
            <div className="text-xs text-ink-muted">Date Code {ro.dateCode || "—"}</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink-muted mb-1.5">Order Information</div>
            <div className="text-xs text-ink-muted mt-0.5">
              Created <span className="text-ink font-semibold">{ro.createdDate.slice(0, 10)}</span>
            </div>
            <div className="text-xs text-ink-muted">
              Promised <span className="text-ink font-semibold">{ro.promisedDate}</span>
            </div>
            <div className="text-xs text-ink-muted">
              Delivery <span className="text-ink font-semibold">{ro.deliveryMethod}</span>
            </div>
            <div className="text-xs text-ink-muted">
              Priority <span className="text-ink font-semibold">{ro.priority}</span>
            </div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink-muted mb-1.5">Repair Type</div>
            <div className="text-sm font-semibold">{ro.serviceType}</div>
            <div className="text-xs text-ink-muted mt-0.5">
              ${ro.grcPrice.toFixed(2)} {ro.grcIsPlaceholder && <span className="text-status-orange font-bold">(placeholder)</span>}
            </div>
            <div className="text-xs text-ink-muted mt-1.5">Technician: {ro.technician ?? "Unassigned"}</div>
          </div>
        </div>

        {ro.toolFailure && (
          <div className="mt-3.5 text-sm">
            <div className="text-ink-muted text-xs">Reported Failure</div>
            <div>{ro.toolFailure}</div>
          </div>
        )}

        <div className="flex gap-2.5 mt-4">
          {ro.status === "Open" && (
            <button className="btn-primary" disabled={busy} onClick={() => void transition("start")}>
              <PlayCircle size={16} /> Start Repair
            </button>
          )}
          {ro.status === "In Progress" && (
            <>
              <button className="btn-outline" disabled={busy} onClick={() => void transition("pause")}>
                <PauseCircle size={16} /> Pause
              </button>
              <button className="btn-primary" disabled={busy || !allPass} title={!allPass ? "All safety checks must Pass first" : undefined} onClick={() => void transition("complete")}>
                <CheckCircle2 size={16} /> Complete Repair
              </button>
            </>
          )}
          {ro.status === "Complete" && (
            <button className="btn-primary" onClick={() => navigate(`/repair-orders/${ro.id}/payment`)}>
              Go to Payment
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 bg-surface rounded-md p-1 w-fit max-w-full overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded text-xs font-bold whitespace-nowrap transition-colors ${
              tab === t ? "bg-brand-blue text-white" : "text-ink-muted hover:text-ink"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Work Order" && (
        <div className="flex flex-col gap-5">
          <div className="card">
            <div className="flex items-center justify-between px-5 pt-5 pb-2">
              <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted">Parts Used</h2>
            </div>
            <div className="overflow-hidden rounded-b-lg">
              <DataTable<PartUsed> columns={partColumns} rows={ro.partsUsed} rowKey={(p) => p.id} emptyMessage="No parts added yet." />
            </div>
            {ro.status !== "Complete" && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end border-t border-border p-5">
                <PartCatalogPicker
                  value={partNumber}
                  onChange={setPartNumber}
                  onSelect={(p) => {
                    setPartNumber(p.partNumber);
                    setPartDesc(p.description);
                    setPartListPrice(p.listPrice);
                    setPartStdCost(p.stdCost);
                  }}
                />
                <input className="input col-span-2" placeholder="Description" value={partDesc} onChange={(e) => setPartDesc(e.target.value)} />
                <input type="number" min={1} className="input col-span-2 sm:col-span-1" placeholder="Qty" value={partQty} onChange={(e) => setPartQty(Number(e.target.value) || 1)} />
                <input type="number" step={0.01} className="input col-span-2 sm:col-span-1" placeholder="List $" value={partListPrice || ""} onChange={(e) => setPartListPrice(Number(e.target.value) || 0)} />
                <button className="btn-outline col-span-2 sm:col-span-5 justify-center" disabled={busy} onClick={() => void addPart()}>
                  <Plus size={15} /> Add Part
                </button>
              </div>
            )}
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3 flex items-center gap-2">
              <Sparkles size={15} /> Customer Update (AI Draft)
            </h2>
            <button className="btn-outline" disabled={aiBusy} onClick={() => void generateAiSummary()}>
              <Sparkles size={15} /> {aiBusy ? "Generating…" : "Generate Customer Update"}
            </button>
            {aiSummary && (
              <div className="mt-3.5">
                <p className="text-sm bg-surface-muted rounded-lg p-3.5">{aiSummary}</p>
                <p className="text-[11px] text-ink-muted mt-2">AI-drafted for review — not sent to the customer automatically.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "Safety Checks & Inspections" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3 flex items-center gap-2">
              <ShieldCheck size={16} /> Safety Checklist
            </h2>
            <p className="text-xs text-ink-muted mb-3.5">Every check must Pass before this repair can be marked complete.</p>
            <div className="flex flex-col gap-2.5">
              {SAFETY_CHECK_KEYS.map((key) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{SAFETY_LABELS[key]}</span>
                  <select
                    className="input w-32"
                    value={ro.safetyChecks[key]}
                    disabled={ro.status === "Complete"}
                    onChange={(e) => void setCheck(key, e.target.value as SafetyCheckState)}
                  >
                    <option>Pending</option>
                    <option>Pass</option>
                    <option>Fail</option>
                  </select>
                </div>
              ))}
            </div>
            {!allPass && ro.status === "In Progress" && (
              <div className="mt-3.5 text-[11px] font-semibold text-status-orange">All checks must read Pass before completing this repair.</div>
            )}
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3 flex items-center gap-2">
              <ClipboardList size={16} /> Notes & Inspection
            </h2>
            <div className="text-xs text-ink-muted mb-1">Notes</div>
            <p className="text-sm mb-3.5">{ro.publicNotes || "—"}</p>
            <div className="grid grid-cols-2 gap-3 text-sm border-t border-border pt-3.5">
              <div>
                <div className="text-xs text-ink-muted">Inspection Date</div>
                <div className="font-semibold">{ro.inspectionDate ?? "—"}</div>
              </div>
              <div>
                <div className="text-xs text-ink-muted">Inspected By</div>
                <div className="font-semibold">{ro.inspectedBy || "—"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "History" && (
        <div className="card overflow-hidden">
          <div className="px-5 pt-5 pb-2">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted flex items-center gap-2">
              <HistoryIcon size={16} /> History
            </h2>
            <p className="text-xs text-ink-muted mt-1">Every real, audited action taken on this repair order.</p>
          </div>
          {historyLoading ? (
            <div className="text-sm text-ink-muted text-center py-10">Loading history…</div>
          ) : !history || history.length === 0 ? (
            <div className="text-sm text-ink-muted text-center py-10">No history recorded yet.</div>
          ) : (
            <div className="flex flex-col px-5 pb-5">
              {history.map((h) => (
                <div key={h.id} className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
                  <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 bg-status-blue-bg text-brand-blue-dark mt-0.5">
                    <HistoryIcon size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{ACTION_LABELS[h.actionType] ?? h.actionType}</div>
                    <div className="text-xs text-ink-muted">
                      {h.actorName ?? "System"} · {h.timestamp.slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
